import { useMemo, useState } from 'react';
import { DeclarationCountry } from 'store/declarations/enums/common/declaration-country';
import { DeclarationInternalType } from 'store/declarations/enums/common/declaration-internal-type';
import { AISMessageTypes, MessageTypes } from 'store/declarations/enums/common/declaration-types';
import { TEMPLATE_FORM_VALUES_MAP } from './constants';

export interface TemplateFilterInitials {
    country?: DeclarationCountry;
    internalType?: DeclarationInternalType;
    formType?: MessageTypes;
}

const useTemplateFilters = (initials: TemplateFilterInitials) => {
    const [country, setCountry] = useState<DeclarationCountry>(initials.country || DeclarationCountry.IRELAND);
    const [internalType, setInternalType] = useState<DeclarationInternalType>(
        initials.internalType || DeclarationInternalType.IMPORT
    );
    const [formType, setFormType] = useState<MessageTypes>(initials.formType || AISMessageTypes.H1);
    const [search, setSearch] = useState<string>('');

    const countryValues =
        TEMPLATE_FORM_VALUES_MAP[country.toUpperCase()] ??
        TEMPLATE_FORM_VALUES_MAP[DeclarationCountry.IRELAND.toUpperCase()];

    const internalTypeOptions = useMemo(
        () => Object.keys(countryValues).map((type) => ({ value: type, label: type.toUpperCase() })),
        [countryValues]
    );

    const formTypeOptions = useMemo(
        () => countryValues[internalType]?.map((type) => ({ value: type, label: type })),
        [countryValues, internalType]
    );

    const changeCountry = (newCountry: DeclarationCountry) => {
        const newCountryValues = TEMPLATE_FORM_VALUES_MAP[newCountry.toUpperCase()];
        const defaultInternalType = Object.keys(newCountryValues)[0];
        const defaultFormType = newCountryValues[defaultInternalType][0];

        setCountry(newCountry);
        setInternalType(defaultInternalType as DeclarationInternalType);
        setFormType(defaultFormType as MessageTypes);
        setSearch('');
    };

    const changeInternalType = (newInternalType: string | number) => {
        const defaultFormType = countryValues[newInternalType][0];

        setInternalType(newInternalType as DeclarationInternalType);
        setFormType(defaultFormType as MessageTypes);
    };

    const changeFormType = (newFormType: string | number) => {
        setFormType(newFormType as MessageTypes);
    };

    return {
        country,
        internalType,
        formType,
        search,
        internalTypeOptions,
        formTypeOptions,
        changeCountry,
        changeInternalType,
        changeFormType,
        changeSearch: setSearch,
    };
};

export type TemplateFilters = ReturnType<typeof useTemplateFilters>;

export default useTemplateFilters;
